// src/belediye/redux/slices/belediyeApplicationSmsSlice.js
// Başvuru Detay -> Vatandaşa SMS Paneli (sohbet) + kullanıcı şablonları
//
// Notlar:
// - Şablonlar user bazlıdır (backend owner_user ile sınırlar)
// - Mesaj geçmişi Notification kaydıdır (application bazlı filtre ile çekilir)
// - UI: panel open/close state'i burada tutulur

import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  fetchSmsTemplatesNormalized,
  createSmsTemplate,
  updateSmsTemplate,
  deleteSmsTemplate,
  sendManualSms,
  fetchApplicationSms, // GET /notifications/notifications/manual-sms/?application_id=...
} from "../../services/notificationsApi";

/**
 * Debug log (opsiyonel)
 * .env: REACT_APP_DEBUG_SMS=1
 */
const SMS_DEBUG = String(process.env.REACT_APP_DEBUG_SMS || "").trim() === "1";
const log = (...args) => {
  if (SMS_DEBUG) console.log(...args);
};

/**
 * sent_at null olabilen kayıtlar için stabil sıralama:
 * - sent_at dolu olanları eski->yeni sıralar
 * - sent_at boş olanları sona iter (pending vb.)
 */
const sortMessagesSafe = (list) => {
  const arr = Array.isArray(list) ? [...list] : [];
  return arr.sort((a, b) => {
    const ta = a?.sent_at ? new Date(a.sent_at).getTime() : null;
    const tb = b?.sent_at ? new Date(b.sent_at).getTime() : null;

    // ikisi de null -> aynı
    if (ta === null && tb === null) return 0;
    // a null, b dolu -> a sona
    if (ta === null && tb !== null) return 1;
    // a dolu, b null -> b sona
    if (ta !== null && tb === null) return -1;

    // ikisi de dolu
    return ta - tb;
  });
};

const toIntIfPossible = (v) => {
  if (typeof v === "number") return v;
  if (typeof v === "string" && v.trim() !== "") {
    const n = Number(v);
    return Number.isNaN(n) ? v : n;
  }
  return v;
};

// ------------------------------
// Async thunks
// ------------------------------

export const loadSmsTemplates = createAsyncThunk(
  "belediyeApplicationSms/loadSmsTemplates",
  async (params, { rejectWithValue }) => {
    try {
      // params opsiyonel: { unit, is_active } gibi
      log("[belediyeApplicationSms] loadSmsTemplates start", params);
      const normalized = await fetchSmsTemplatesNormalized(params);
      const list = normalized?.results || [];
      log("[belediyeApplicationSms] loadSmsTemplates ok", { count: normalized?.count, listLen: list.length });
      return list;
    } catch (err) {
      const api = err?.response?.data;
      const msg =
        api?.detail ||
        err?.message ||
        "SMS şablonları alınamadı.";
      log("[belediyeApplicationSms] loadSmsTemplates fail", { msg, api });
      return rejectWithValue(msg);
    }
  }
);

export const addSmsTemplate = createAsyncThunk(
  "belediyeApplicationSms/addSmsTemplate",
  async (payload, { rejectWithValue }) => {
    try {
      // payload: { title, template_text | body, unit?, is_active? }
      log("[belediyeApplicationSms] addSmsTemplate start", payload);
      const data = await createSmsTemplate(payload);
      log("[belediyeApplicationSms] addSmsTemplate ok", data);
      return data;
    } catch (err) {
      const api = err?.response?.data;
      const msg =
        api?.detail ||
        err?.message ||
        "SMS şablonu oluşturulamadı.";
      log("[belediyeApplicationSms] addSmsTemplate fail", { msg, api });
      return rejectWithValue(msg);
    }
  }
);

export const editSmsTemplate = createAsyncThunk(
  "belediyeApplicationSms/editSmsTemplate",
  async ({ id, patch }, { rejectWithValue }) => {
    try {
      if (!id) throw new Error("editSmsTemplate: id zorunlu");
      log("[belediyeApplicationSms] editSmsTemplate start", { id, patch });
      const data = await updateSmsTemplate(id, patch);
      log("[belediyeApplicationSms] editSmsTemplate ok", data);
      return data;
    } catch (err) {
      const api = err?.response?.data;
      const msg =
        api?.detail ||
        err?.message ||
        "SMS şablonu güncellenemedi.";
      log("[belediyeApplicationSms] editSmsTemplate fail", { msg, api });
      return rejectWithValue(msg);
    }
  }
);

export const removeSmsTemplate = createAsyncThunk(
  "belediyeApplicationSms/removeSmsTemplate",
  async (id, { rejectWithValue }) => {
    try {
      if (!id) throw new Error("removeSmsTemplate: id zorunlu");
      log("[belediyeApplicationSms] removeSmsTemplate start", { id });
      await deleteSmsTemplate(id);
      log("[belediyeApplicationSms] removeSmsTemplate ok", { id });
      return id;
    } catch (err) {
      const api = err?.response?.data;
      const msg =
        api?.detail ||
        err?.message ||
        "SMS şablonu silinemedi.";
      log("[belediyeApplicationSms] removeSmsTemplate fail", { msg, api });
      return rejectWithValue(msg);
    }
  }
);

export const loadApplicationSms = createAsyncThunk(
  "belediyeApplicationSms/loadApplicationSms",
  async (applicationId, { rejectWithValue }) => {
    try {
      const appId = toIntIfPossible(applicationId);
      log("[belediyeApplicationSms] loadApplicationSms start", { applicationId: appId });

      // Backend: GET /notifications/notifications/manual-sms/?application_id=...
      // Tipik dönüş: Notification[] (array)
      const data = await fetchApplicationSms(appId);
      const list = Array.isArray(data) ? data : (data?.results || []);

      log("[belediyeApplicationSms] loadApplicationSms ok", { appId, listLen: list.length });
      return { applicationId: appId, list };
    } catch (err) {
      const api = err?.response?.data;
      const msg =
        api?.detail ||
        err?.message ||
        "Başvuru SMS geçmişi alınamadı.";
      log("[belediyeApplicationSms] loadApplicationSms fail", { msg, api });
      return rejectWithValue(msg);
    }
  }
);

export const sendApplicationSms = createAsyncThunk(
  "belediyeApplicationSms/sendApplicationSms",
  /**
   * payload:
   * {
   *   application_id: number,
   *   template_id?: number,
   *   text?: string,
   *   message?: string, // destek (service normalize eder)
   *   save_as_template?: boolean,
   *   template_title?: string
   * }
   */
  async (payload, { rejectWithValue }) => {
    try {
      log("[belediyeApplicationSms] sendApplicationSms start", payload);
      const data = await sendManualSms(payload);
      log("[belediyeApplicationSms] sendApplicationSms ok", data);
      return data;
    } catch (err) {
      const api = err?.response?.data;

      // DRF validation error: { field: ["..."] } olabilir
      const msg =
        api?.detail ||
        (api && typeof api === "object"
          ? Object.entries(api)
              .map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(", ") : String(v)}`)
              .join(" | ")
          : null) ||
        err?.message ||
        "SMS gönderilemedi.";

      log("[belediyeApplicationSms] sendApplicationSms fail", { msg, api });
      return rejectWithValue(msg);
    }
  }
);

// ------------------------------
// Initial state
// ------------------------------

const initialState = {
  // UI
  isPanelOpen: false,
  activeApplicationId: null,

  // Templates (user bazlı)
  templates: [],
  templatesLoading: false,

  // Messages (application bazlı)
  messagesByApplicationId: {}, // { [appId]: Notification[] }
  messagesLoadingByApplicationId: {}, // { [appId]: boolean }

  // Send state
  sending: false,

  // Errors
  templatesError: null,
  messagesErrorByApplicationId: {}, // { [appId]: string|null }
  sendError: null,
};

// ------------------------------
// Slice
// ------------------------------

const belediyeApplicationSmsSlice = createSlice({
  name: "belediyeApplicationSms",
  initialState,
  reducers: {
    openSmsPanel: (state, action) => {
      const applicationId = toIntIfPossible(action.payload);
      state.isPanelOpen = true;
      state.activeApplicationId = applicationId ?? null;
      state.sendError = null;
    },
    closeSmsPanel: (state) => {
      state.isPanelOpen = false;
      state.activeApplicationId = null;
      state.sendError = null;
    },
    clearSmsErrors: (state) => {
      state.templatesError = null;
      state.sendError = null;
      state.messagesErrorByApplicationId = {};
    },
    // Panel içinde optimistic append yapmak isterseniz (opsiyonel)
    appendLocalMessage: (state, action) => {
      const { applicationId, message } = action.payload || {};
      const appId = toIntIfPossible(applicationId);
      if (!appId || !message) return;
      const cur = state.messagesByApplicationId[appId] || [];
      state.messagesByApplicationId[appId] = [...cur, message];
    },
  },
  extraReducers: (builder) => {
    // -------- Templates --------
    builder
      .addCase(loadSmsTemplates.pending, (state) => {
        state.templatesLoading = true;
        state.templatesError = null;
      })
      .addCase(loadSmsTemplates.fulfilled, (state, action) => {
        state.templatesLoading = false;
        state.templates = action.payload || [];
      })
      .addCase(loadSmsTemplates.rejected, (state, action) => {
        state.templatesLoading = false;
        state.templatesError = action.payload || "SMS şablonları alınamadı.";
      });

    builder
      .addCase(addSmsTemplate.pending, (state) => {
        state.templatesError = null;
      })
      .addCase(addSmsTemplate.fulfilled, (state, action) => {
        const tpl = action.payload;
        if (tpl?.id) state.templates = [tpl, ...state.templates];
      })
      .addCase(addSmsTemplate.rejected, (state, action) => {
        state.templatesError = action.payload || "SMS şablonu oluşturulamadı.";
      });

    builder
      .addCase(editSmsTemplate.fulfilled, (state, action) => {
        const upd = action.payload;
        if (!upd?.id) return;
        state.templates = state.templates.map((t) => (t.id === upd.id ? upd : t));
      })
      .addCase(editSmsTemplate.rejected, (state, action) => {
        state.templatesError = action.payload || "SMS şablonu güncellenemedi.";
      });

    builder
      .addCase(removeSmsTemplate.fulfilled, (state, action) => {
        const id = action.payload;
        state.templates = state.templates.filter((t) => t.id !== id);
      })
      .addCase(removeSmsTemplate.rejected, (state, action) => {
        state.templatesError = action.payload || "SMS şablonu silinemedi.";
      });

    // -------- Messages --------
    builder
      .addCase(loadApplicationSms.pending, (state, action) => {
        const appId = toIntIfPossible(action.meta.arg);
        state.messagesLoadingByApplicationId[appId] = true;
        state.messagesErrorByApplicationId[appId] = null;
      })
      .addCase(loadApplicationSms.fulfilled, (state, action) => {
        const { applicationId, list } = action.payload || {};
        const appId = toIntIfPossible(applicationId);
        if (!appId) return;

        state.messagesLoadingByApplicationId[appId] = false;
        state.messagesByApplicationId[appId] = sortMessagesSafe(list || []);
      })
      .addCase(loadApplicationSms.rejected, (state, action) => {
        const appId = toIntIfPossible(action.meta.arg);
        state.messagesLoadingByApplicationId[appId] = false;
        state.messagesErrorByApplicationId[appId] =
          action.payload || "Başvuru SMS geçmişi alınamadı.";
      });

    // -------- Send --------
    builder
      .addCase(sendApplicationSms.pending, (state) => {
        state.sending = true;
        state.sendError = null;
      })
      .addCase(sendApplicationSms.fulfilled, (state, action) => {
        state.sending = false;

        const notif = action.payload;
        const appIdFromResponse = toIntIfPossible(notif?.application);
        const targetAppId = state.activeApplicationId || appIdFromResponse;

        if (targetAppId) {
          const cur = state.messagesByApplicationId[targetAppId] || [];
          const next = [...cur, notif];
          state.messagesByApplicationId[targetAppId] = sortMessagesSafe(next);
        }
      })
      .addCase(sendApplicationSms.rejected, (state, action) => {
        state.sending = false;
        state.sendError = action.payload || "SMS gönderilemedi.";
      });
  },
});

// ------------------------------
// Exports
// ------------------------------

export const {
  openSmsPanel,
  closeSmsPanel,
  clearSmsErrors,
  appendLocalMessage,
} = belediyeApplicationSmsSlice.actions;

export default belediyeApplicationSmsSlice.reducer;

// ------------------------------
// Selectors
// ------------------------------

export const selectSmsPanelOpen = (state) => state.belediyeApplicationSms?.isPanelOpen;
export const selectSmsActiveApplicationId = (state) =>
  state.belediyeApplicationSms?.activeApplicationId;

export const selectSmsTemplates = (state) => state.belediyeApplicationSms?.templates || [];
export const selectSmsTemplatesLoading = (state) =>
  !!state.belediyeApplicationSms?.templatesLoading;
export const selectSmsTemplatesError = (state) =>
  state.belediyeApplicationSms?.templatesError;

export const selectSmsMessagesForApplication = (state, applicationId) => {
  const appId = toIntIfPossible(applicationId);
  const map = state.belediyeApplicationSms?.messagesByApplicationId || {};
  return map[appId] || [];
};
export const selectSmsMessagesLoading = (state, applicationId) => {
  const appId = toIntIfPossible(applicationId);
  const map = state.belediyeApplicationSms?.messagesLoadingByApplicationId || {};
  return !!map[appId];
};
export const selectSmsMessagesError = (state, applicationId) => {
  const appId = toIntIfPossible(applicationId);
  const map = state.belediyeApplicationSms?.messagesErrorByApplicationId || {};
  return map[appId] || null;
};

export const selectSmsSending = (state) => !!state.belediyeApplicationSms?.sending;
export const selectSmsSendError = (state) => state.belediyeApplicationSms?.sendError;
